import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { TravelerRoomUI } from '@pax/entities/pax-ui';
import {
  loadSearchDataSuccessAction, saveSearchDataAction
} from '@shared/base-search-bar/store/actions/storage.actions';
import { guestListUI, setRoomGuests, setRoomGuestsByDocument } from '@pax/mappers/guest-list-ui.mapper';
import * as actions from '@pax/store/actions/pax.actions';
import { getDefaultDataIndex, getDefaultPaxData, getDocumentData } from '@pax/mappers/default-pax.mapper';
import {
  setDefaultWithNewLoyaltyByGuest, setDefaultWithNewLoyaltySelectionByGuest, setDefaultWithSuccessLoyaltyByGuest,
  setDefaultWithSuccessLoyaltyDeleteByGuest, setDefaultWithSuccessLoyaltyUpdateBuGuest
} from '@pax/mappers/loyalty.mapper';
import { isNullOrUndefined } from '@app/util/util';

export interface GuestListUIState extends EntityState<TravelerRoomUI> { }

function getId(traveler: TravelerRoomUI): number {
  return traveler.id;
}

export const getEntityById = (id: number) => (state: GuestListUIState) => state.entities[id];

export const guestListUIAdapter: EntityAdapter<TravelerRoomUI> = createEntityAdapter<TravelerRoomUI>({
  selectId: getId
});

export const initialState = guestListUIAdapter.getInitialState({});

const featureReducer = createReducer(
  initialState,
  on(saveSearchDataAction, (state, { data, searchBy }) => {
    const arrangement = data ? data.guestsPerRoom : [];
    const guestsPerRoom = [].concat(...arrangement);
    return guestListUIAdapter.setAll(guestListUI(guestsPerRoom, searchBy), state);
  }),
  on(loadSearchDataSuccessAction, (state, { data, searchBy }) => {
    const arrangement = data ? data.guestsPerRoom : [];
    const guestsPerRoom = [].concat(...arrangement);
    return guestListUIAdapter.setAll(guestListUI(guestsPerRoom, searchBy), state);
  }),
  on(actions.configDefaultGuestUI, (state, { config }) => {
    const { response, user, isInternationalSearch, documents, rules } = config;
    const defaultDataId = getDefaultDataIndex({ travelers: response, user, isInternationalSearch, documents, rules });
    if (defaultDataId >= 0) {
      const documentData = getDocumentData(response[defaultDataId]);
      const data = getDefaultPaxData(response[defaultDataId], null, true);
      return guestListUIAdapter.updateOne(
        {
          id: 0, changes: {
            roomGuests: [...setRoomGuests(getEntityById(0)(state), 0, {
              ...data, ...documentData
            }, null, true)]
          }
        }, state
      );
    } else return state;
  }),
  on(actions.setGuestDefaultDataUIAction, (state, { payload, roomId, guestId }) => {
    return guestListUIAdapter.updateOne({
      id: roomId, changes: {
        roomGuests: [...setRoomGuests(getEntityById(roomId)(state), guestId, payload)]
      }
    }, state);
  }),
  on(actions.deletePaxSuccessAction, (state, { payload, id, guestId }) => {
    if (!isNullOrUndefined(guestId)) {
      return guestListUIAdapter.updateOne({
        id, changes: {
          roomGuests: [...setRoomGuests(getEntityById(id)(state), guestId, null, payload)]
        }
      }, state);
    } else return state;
  }),
  on(actions.deletePaxDocumentSuccessAction, (state, { payload, id, guestId }) => {
    if (!isNullOrUndefined(guestId)) {
      return guestListUIAdapter.updateOne({
        id, changes: {
          roomGuests: [...setRoomGuestsByDocument(getEntityById(id)(state), guestId, payload)]
        }
      }, state);
    } else return state;
  }),
  on(actions.setTavelerPaxAction, (state, {config }) => {
    const { payload, id, guestId } = config;
    if (!isNullOrUndefined(guestId)) {
      return guestListUIAdapter.updateOne({
        id, changes: {
          roomGuests: [...setRoomGuests(getEntityById(id)(state), guestId, payload)]
        }
      }, state);
    } else return state;
  }),
  on(actions.removeDefaultPaxByGuestRoomAction, (state, { roomIndex, guestId }) => {
    if (!isNullOrUndefined(guestId)) {
      return guestListUIAdapter.updateOne({
        id: roomIndex, changes: {
          roomGuests: [...setRoomGuests(getEntityById(roomIndex)(state), guestId, null)]
        }
      }, state);
    } else return state;
  }),
  on(actions.addPackageLoyaltyProgramAction, (state, { payload, id, guestId }) => {
    if (!isNullOrUndefined(guestId)) {
      const entity = getEntityById(id)(state);
      const defaultData = { ...setDefaultWithNewLoyaltyByGuest(entity, payload, guestId) };
      return guestListUIAdapter.updateOne({
        id, changes: {
          roomGuests: [...setRoomGuests(entity, guestId, defaultData)]
        }
      }, state);
    } else return state;
  }),
  on(actions.createPackageLoyaltyProgramSuccessAction, (state, { response, id, guestId }) => {
    if (!isNullOrUndefined(guestId)) {
      const entity = getEntityById(id)(state);
      const defaultData = { ...setDefaultWithSuccessLoyaltyByGuest(entity, response, guestId) };
      return guestListUIAdapter.updateOne({
        id, changes: {
          roomGuests: [...setRoomGuests(entity, guestId, defaultData)]
        }
      }, state);
    } else return state;
  }),
  on(actions.setPackageLoyaltyProgramAction, (state, { payload, id, guestId }) => {
    const entity = getEntityById(id)(state);
    const defaultData = { ...setDefaultWithNewLoyaltySelectionByGuest(entity, payload, guestId) };
    return guestListUIAdapter.updateOne({
      id, changes: {
        roomGuests: [...setRoomGuests(entity, guestId, defaultData)]
      }
    }, state);
  }),
  on(actions.deletePackageLoyaltyProgramSuccessAction, (state, { response, id, guestId }) => {
    const entity = getEntityById(id)(state);
    const defaultData = { ...setDefaultWithSuccessLoyaltyDeleteByGuest(entity, response, guestId) };
    return guestListUIAdapter.updateOne({
      id, changes: {
        roomGuests: [...setRoomGuests(entity, guestId, defaultData)]
      }
    }, state);
  }),
  on(actions.updatePackageLoyaltyProgramSuccessAction, (state, { response, id, guestId }) => {
    if (!isNullOrUndefined(guestId)) {
      const entity = getEntityById(id)(state);
      const defaultData = { ...setDefaultWithSuccessLoyaltyUpdateBuGuest(entity, response, guestId) };
      return guestListUIAdapter.updateOne({
        id, changes: {
          roomGuests: [...setRoomGuests(entity, guestId, defaultData)]
        }
      }, state);
    } else return state;
  }),
);

export const guestListUIReducer = (state: GuestListUIState, action: Action): GuestListUIState => {
  return featureReducer(state, action);
};
